<template>
    <div
        id="sidebar" :class="{ open: menu.isOpen || menu.hoverActive }"
        v-click-outside="handleOutsideClick"
    >
        <ModulesDropdown
            :class="{ active: moduleActive }"
            @selected="() => (moduleActive = false)"
            @close="() => (moduleActive ? (moduleActive = false) : null)"
            :active="moduleActive"
        />
        <Modal2
            class-name="rep-settings-modal"
            v-if="repSettingsModal"
            :title="$t('message.represantativeSettings')"
            :show-footer="false"
            @cancel="repSettingsModal = false"
        >
            <RepresentativeSettings />
        </Modal2>
        <div class="content">
            <div
                class="menu-list"
                v-if="permissions.length || isAdmin"
                :key="'menu-' + forceRenderCount"
                ref="scrollRef"
                @mouseenter="mouseEnterStart"
                @mouseleave="mouseLeaveStart"
            >
                <template v-for="item in allowedRoutes()">
                    <a-tooltip
                        :title="item.title"
                        placement="right"
                        :key="item.key"
                        :overlayClassName="menu.isOpen || menu.hoverActive ? ' hiddenToolTip' : ''"
                        @click="closeMenu"
                    >
                        <router-link :to="item.path" class="menu-list-item" @click.native="handleMenuClick(item, $event)">
                            <div class="menu-list-item-icon">
                                <div v-if="item.isNew" class="new-tag">{{ $t("message.sidebarNewMenuBadge") }}</div>
                                <a-icon
                                    :type="item.antIcon"
                                    v-if="item.antIcon"
                                    style="font-size: 20px"
                                    :style="{ color: bugExist ? 'red !important' : '#9ba2b6 !important' }"
                                />
                                <component v-bind:is="item.icon" class="myIcon" v-else />
                            </div>
                            <div class="menu-list-item-title">{{ item.title }}</div>
                        </router-link>
                    </a-tooltip>
                </template>

                <!-- Web Sekmeler -->
                <template v-for="tab in visibleWebTabs">
                    <a-tooltip
                        :title="tab.description || tab.name"
                        placement="right"
                        :key="'webtab-' + tab._id"
                        :overlayClassName="menu.isOpen || menu.hoverActive ? ' hiddenToolTip' : ''"
                        @click="closeMenu"
                    >
                        <router-link :to="'/webtab/' + tab._id" class="menu-list-item" @click.native="handleWebTabClick(tab, $event)">
                            <div class="menu-list-item-icon">
                                <span
                                    class="material-icons"
                                    :style="{ color: tab.iconColor || '#6b727d', fontSize: '20px' }"
                                >
                                    {{ tab.icon }}
                                </span>
                            </div>
                            <div class="menu-list-item-title">{{ tab.name }}</div>
                        </router-link>
                    </a-tooltip>
                </template>
            </div>
            <div class="dropdown-control">
                <div class="dropdown-control-items" @mouseenter="mouseEnterStart" @mouseleave="mouseLeaveStart">
                    <a-tooltip
                        v-if="modules.length > 0"
                        :title="$t('modules.modules')"
                        placement="right"
                        :overlayClassName="menu.isOpen || menu.hoverActive ? ' hiddenToolTip' : ''"
                    >
                        <div class="dropdown-control-item modules" @click="moduleActive = !moduleActive">
                            <div class="dropdown-control-item-icon">
                                <component v-bind:is="'ModuleIcon'" class="myIcon" />
                            </div>
                            <div class="information-text">{{ $t("modules.modules") }}</div>
                        </div>
                    </a-tooltip>

                    <a-tooltip
                        :title="$t('message.integrations')"
                        placement="right"
                        :overlayClassName="menu.isOpen || menu.hoverActive ? ' hiddenToolTip' : ''"
                    >
                        <router-link
                            :to="'/integrations'"
                            class="dropdown-control-item integrations"
                            v-if="isAllowed('integrations.view')"
                        >
                            <div class="dropdown-control-item-icon">
                                <component v-bind:is="'IntegrationIcon'" class="myIcon" />
                            </div>
                            <div class="information-text">{{ $t("message.integrations") }}</div>
                        </router-link>
                    </a-tooltip>
                    <a-tooltip
                        :title="$t('report.reports')"
                        placement="right"
                        :overlayClassName="menu.isOpen || menu.hoverActive ? ' hiddenToolTip' : ''"
                    >
                        <router-link
                            :to="'/report-new'"
                            class="dropdown-control-item integrations"
                            v-if="isAllowed('report.get')"
                        >
                            <div class="dropdown-control-item-icon">
                                <component v-bind:is="'DetailReports'" class="myIcon" />
                            </div>
                            <div class="information-text">{{ $t("report.reports") }}</div>
                        </router-link>
                    </a-tooltip>
                    <a-tooltip
                        :title="$t('message.settings')"
                        placement="right"
                        :overlayClassName="menu.isOpen || menu.hoverActive ? ' hiddenToolTip' : ''"
                    >
                        <router-link :to="'/settings'" class="dropdown-control-item settings" @click="closeMenu">
                            <div class="dropdown-control-item-icon">
                                <component v-bind:is="'SettingsIcon'" class="myIcon" />
                            </div>
                            <div class="information-text">{{ $t("message.settings") }}</div>
                        </router-link>
                    </a-tooltip>
                    <Popover placement="rightBottom" class-name="user-popover" trigger="click">
                        <template #content>
                            <div class="dropdown-control-item profile" v-if="user && !isAdmin">
                                <div
                                    class="dropdown-control-item-icon profile-avatar"
                                    :class="user.status.toLowerCase()"
                                >
                                    <div class="profile-avatar-badge"></div>
                                    <div class="avatar">
                                        <a-avatar
                                            v-if="!user.gravatar"
                                            :size="30"
                                            icon="user"
                                            :username="user.name"
                                            :round="false"
                                            :background-color="''"
                                        />
                                        <img v-else-if="user.gravatar" :src="user.gravatar" :alt="user.name" />
                                        <span v-else>{{ user.name.substr(0, 1).toUpperCase() }}</span>
                                    </div>
                                </div>
                                <div class="information-text">
                                    {{ user.name.charAt(0).toUpperCase() + nameSorter(user.name.slice(1)) }}
                                </div>
                            </div>
                        </template>
                        <template #popover-content>
                            <div class="user-popover-content">
                                <div class="user-popover-content-profile" v-if="user && !isAdmin">
                                    <div
                                        class="user-popover-content-profile-content"
                                        :class="user.status.toLowerCase()"
                                    >
                                        <div class="user-popover-content-profile-content-avatar">
                                            <div class="avatar">
                                                <a-avatar
                                                    v-if="!user.gravatar"
                                                    :size="30"
                                                    icon="user"
                                                    :username="user.name"
                                                    :round="false"
                                                    :background-color="''"
                                                />
                                                <img v-else-if="user.gravatar" :src="user.gravatar" :alt="user.name" />
                                                <span v-else>{{ user.name.substr(0, 1).toUpperCase() }}</span>
                                            </div>
                                        </div>
                                        <div class="user-popover-content-profile-content-name">
                                            {{ user.name.charAt(0).toUpperCase() + nameSorter(user.name.slice(1)) }}
                                        </div>
                                    </div>
                                    <div class="user-popover-content-profile-options">
                                        <div class="user-popover-content-profile-options-logout" @click="logout">
                                            <div class="material-icons">logout</div>
                                        </div>
                                    </div>
                                </div>

                                <Popover placement="rightBottom" class-name="user-popover" trigger="hover">
                                    <template #content>
                                        <div class="user-popover-content-item">
                                            <div class="user-popover-content-item-icon">
                                                <component :is="status[user.status.toLowerCase()].icon" />
                                            </div>
                                            <div class="user-popover-content-item-text">
                                                {{ status[user.status.toLowerCase()].text }}
                                            </div>
                                            <div class="user-popover-content-item-arrow">
                                                <div class="material-icons">chevron_right</div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #popover-content>
                                        <div class="user-popover-content">
                                            <div class="user-popover-content-item" @click="setStatus('Online')">
                                                <div class="user-popover-content-item-icon">
                                                    <Online />
                                                </div>
                                                <div class="user-popover-content-item-text">
                                                    {{ $t("message.online") }}
                                                </div>
                                            </div>
                                            <div class="user-popover-content-item" @click="setStatus('Away')">
                                                <div class="user-popover-content-item-icon">
                                                    <Away />
                                                </div>
                                                <div class="user-popover-content-item-text">
                                                    {{ $t("message.beRightBack") }}
                                                </div>
                                            </div>
                                            <div class="user-popover-content-item" @click="setStatus('Busy')">
                                                <div class="user-popover-content-item-icon">
                                                    <Busy />
                                                </div>
                                                <div class="user-popover-content-item-text">
                                                    {{ $t("message.busy") }}
                                                </div>
                                            </div>
                                            <div class="user-popover-content-item" @click="setStatus('Offline')">
                                                <div class="user-popover-content-item-icon">
                                                    <Offline />
                                                </div>
                                                <div class="user-popover-content-item-text">
                                                    {{ $t("message.offline") }}
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </Popover>
                                <Popover placement="rightBottom" class-name="user-popover" trigger="click">
                                    <template #content>
                                        <div class="user-popover-content-item language-content">
                                            <div class="user-popover-content-item-icon">
                                                <country-flag
                                                    :class="{ selected: language.code === $i18n.locale }"
                                                    :country="
                                                        language.code === 'en'
                                                            ? 'us'
                                                            : language.code === 'ar'
                                                            ? 'sa'
                                                            : language.code
                                                    "
                                                />
                                            </div>
                                            <div class="user-popover-content-item-text">
                                                {{ $t("message.changeLang") }}
                                            </div>
                                        </div>
                                    </template>
                                    <template #popover-content>
                                        <div
                                            v-for="(language, index) in languages"
                                            :key="'lang-' + index"
                                            class="user-popover-content-item language-content"
                                            @click="setCurrentLanguage(language.code)"
                                        >
                                            <div class="user-popover-content-item-icon">
                                                <country-flag
                                                    :class="{ selected: language.code === $i18n.locale }"
                                                    :country="
                                                        language.code === 'en'
                                                            ? 'us'
                                                            : language.code === 'ar'
                                                            ? 'sa'
                                                            : language.code
                                                    "
                                                />
                                            </div>
                                            <div class="user-popover-content-item-text">
                                                {{ language.name }}
                                            </div>
                                        </div>
                                    </template>
                                </Popover>
                                <router-link to="/profile" class="user-popover-content-item">
                                    <div class="user-popover-content-item-icon">
                                        <div class="material-icons">settings</div>
                                    </div>
                                    <div class="user-popover-content-item-text">
                                        {{ $t("message.represantativeSettings") }}
                                    </div>
                                </router-link>
                                <div class="user-popover-content-item" @click="logout">
                                    <div class="user-popover-content-item-icon">
                                        <div class="material-icons text-danger">logout</div>
                                    </div>
                                    <div class="user-popover-content-item-text text-danger">
                                        {{ $t("message.logout") }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Popover>
                </div>
                <div class="dropdown-control-item btn-sidebar-navigation" @click="clickItem" @touchend="clickItem">
                    <div class="information-text">{{ $t("message.showHide") }}</div>
                    <div>
                        <RightSidebarIcon v-if="menu.isOpen || menu.hoverActive" />
                        <LeftSidebarIcon v-else />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import menu from "../utils/menu";
import Comments from "./svg/Comment";
import History from "./svg/History";
import Visitor from "./svg/Visitor";
import Masks from "./svg/Masks";
import Users from "./svg/User";
import Trigger from "./svg/Trigger";
import Integrations from "./svg/Integrations";
import UserSlash from "./svg/UserSlash";
import ClipboardList from "./svg/ClipboardList";
import BoxOpen from "./svg/BoxOpen";
import HandPaper from "./svg/HandPaper";
import Ban from "./svg/Ban";
import Arrows from "./svg/Arrows";
import Cogs from "./svg/Cogs";
import Bug from "./svg/Bug";
import WindowOpen from "./svg/WindowOpen";
import Calendar from "./svg/Calendar";
import ClusterDashboard from "./svg/ClusterDashboard";
import Site from "./svg/Site";
import { log2 } from "../utils/log2";
import OfflineSupport from "./svg/OfflineSupport";
import DetailReports from "./svg/DetailReports";
import Catalog from "./svg/Catalog";
import Chatbot from "./svg/Chatbot";
import Ticket from "./svg/Ticket";
import Integration from "./svg/Integration";
import CreditCard from "./svg/CreditCard";
import { getPersistenceData } from "../utils/persistenceStorage";
import { SELF_SERVICE_DOMAIN } from "../utils/utils";
import Apps from "./svg/Apps";
import Tags from "./svg/Tags";
import systemPubSub from "../utils/systemPubSub";
import { filter } from "rxjs/operators";
import InstagramIcon from "./svg/InstagramIcon";
import Coupon from "./svg/Coupon";
import AdminReports from "./svg/AdminReports.vue";
import ModulesDropdown from "./ModulesDropdown.vue";
import Popover from "./Popover.vue";
import { WEB_PUSH_NOTIFICATION_SERVICE } from "../push-notification/subscribe";

import Online from "../components/svg/Online";
import Away from "../components/svg/Away";
import Busy from "../components/svg/Busy";
import Offline from "../components/svg/Offline";
import { sendSocketMessage } from "../utils/socketStream";
import CountryFlag from "vue-country-flag";
import Modal2 from "./common/Modal2.vue";
import RepresentativeSettings from "./RepresentativeSettings.vue";
import moment from "moment/moment";

import LeftSidebarIcon from "./svg/sidebar/LeftSidebarIcon.vue";
import RightSidebarIcon from "./svg/sidebar/RightSidebarIcon.vue";
import ChatIcon from "./svg/sidebar/ChatIcon.vue";
import VisitorsIcon from "./svg/sidebar/VisitorsIcon.vue";
import ArchiveIcon from "./svg/sidebar/ArchiveIcon.vue";
import IntegrationIcon from "./svg/sidebar/IntegrationIcon.vue";
import SettingsIcon from "./svg/sidebar/SettingsIcon.vue";
import ModuleIcon from "./svg/sidebar/ModuleIcon.vue";


const log = log2("Sidebar");
export default {
    name: "Sidebar",
    components: {
        RepresentativeSettings,
        Modal2,
        CountryFlag,
        Popover,
        ModulesDropdown,
        Coupon,
        InstagramIcon,
        Integration,
        Apps,
        Comments,
        Chatbot,
        History,
        Masks,
        Users,
        Visitor,
        UserSlash,
        ClipboardList,
        BoxOpen,
        HandPaper,
        Ban,
        Cogs,
        Bug,
        WindowOpen,
        Calendar,
        OfflineSupport,
        DetailReports,
        Catalog,
        ClusterDashboard,
        Site,
        Ticket,
        CreditCard,
        Arrows,
        Trigger,
        Tags,
        Integrations,
        AdminReports,
        Online,
        Away,
        Busy,
        Offline,
        LeftSidebarIcon,
        RightSidebarIcon,
        ChatIcon,
        VisitorsIcon,
        ArchiveIcon,
        IntegrationIcon,
        SettingsIcon,
        ModuleIcon
    },
    data: function () {
        return {
            repSettingsModal: false,
            forceRenderCount: 0,
            timerId: null,
            timerId2: null,
            debugMode: SELF_SERVICE_DOMAIN === location.hostname,
            hoverTimerId: null,
            moduleActive: false,
            unSubs: [],
            isMouseInside: false,
            hoverLock: false,
            buttonOpened: false,
            lastClickTime: 0,
            status: {
                online: {
                    icon: Online,
                    text: this.$t("message.online"),
                },
                offline: {
                    icon: Offline,
                    text: this.$t("message.offline"),
                },
                away: {
                    icon: Away,
                    text: this.$t("message.beRightBack"),
                },
                busy: {
                    icon: Busy,
                    text: this.$t("message.busy"),
                },
            },
        };
    },

    mounted() {
        const firstItem = this.allowedRoutes()[0];
        if (firstItem && this.$route.path === "/denied") {
            this.$router.push(firstItem.path);
        }
        this.timerId = setInterval(() => {
            if (!this.permissions.length && this.isLoggedIn) {
                this.getPermissions({ perPage: 500, page: 1 });
            }
            if (this.permissions.length && this.timerId) {
                clearInterval(this.timerId);
            }
        }, 5000);
        if (this.ticketViewPermissionExists) {
            this.fetchWaitingTickets({
                page: 1,
                perPage: !isNaN(getPersistenceData("ticket-page-size") || "undefined")
                    ? parseInt(getPersistenceData("ticket-page-size"))
                    : 10,
                tag: "all",
            });
            const newTicketSub = systemPubSub.stream.pipe(filter((x) => x.type === "ticket_message")).subscribe((x) => {
                this.fetchWaitingTickets({
                    page: 1,
                    perPage: !isNaN(getPersistenceData("ticket-page-size") || "undefined")
                        ? parseInt(getPersistenceData("ticket-page-size"))
                        : 10,
                    tag: "all",
                });
            });
            this.unSubs.push(() => newTicketSub.unsubscribe());
        }

        // Web sekmelerini yükle
        if (this.isLoggedIn) {
            this.fetchWebTabs().catch((err) => {
                console.error("Web tabs fetch error:", err);
            });
        }
    },
    computed: {
        ...mapState({
            language: (state) => state.language.language,
            languages: (state) => state.language.languages,
            blockedConnection: (state) => state.app.blockedConnection,
            menu: (state) => state.layout.menu,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            bugExist: (state) => state.auth.bugExist,
            adminUser: (state) => state.auth.adminUser,
            isAdmin: (state) => state.auth.adminUser != null,
            permissions: (state) => state.permission.permissions,
            modules: (state) => state.module.modules,
            ticketViewPermissionExists: (state) => {
                const isRoot = _.get(state.auth.user, "role.isRoot");
                return (
                    isRoot ||
                    !!(_.get(state.auth.user, "role.permissions") || []).find(
                        (x) => x.name === "offlineSupportRequest.get"
                    )
                );
            },
        }),
        ...mapGetters({
            isLoggedIn: "auth/isLoggedIn",
            getWaitingTickets: "ticket/getWaitingTickets",
            visibleWebTabs: "webtabs/getMenuWebTabs",
        }),
        isUserRoutes() {
            const userRoutes = ["/users", "/roles", "banned-visitors", "departments"];
            return userRoutes.includes(this.$route.path);
        },
        isContentRoutes() {
            const contentRoutes = ["/templates", "/system-messages", "/catalog,", "/forbidden-inputs,", "/tags,"];
            return contentRoutes.includes(this.$route.path);
        },
    },
    methods: {
        ...mapActions({
            toggleMenu: "layout/toggleMenu",
            toggleHoverMenu: "layout/toggleHoverMenu",
            getPermissions: "permission/getPermissions",
            fetchWaitingTickets: "ticket/fetchWaitingTickets",
            doLogout: "auth/logout",
            setUserStatus: "auth/setStatus",
            setLanguage: "language/setLanguage",
            fetchWebTabs: "webtabs/fetchWebTabs",
        }),
        setCurrentLanguage(languageCode) {
            log.info("setCurrentLanguage", languageCode);
            this.setLanguage(languageCode);
            this.$i18n.locale = this.language.code;
            if (moment.locale() !== languageCode) moment.locale(languageCode);
        },
        setStatus(status) {
            sendSocketMessage("user status-change", {
                userId: this.user._id,
                status: status,
                siteId: this.site._id,
            });
            this.setUserStatus(status);
        },
        logout() {
            if (this.user) {
                WEB_PUSH_NOTIFICATION_SERVICE.unsubscribeFromAllTopic(this.site._id, this.user._id);
            }
            this.doLogout();
            this.$router.push("/login");
        },
        nameSorter(text) {
            if (text.length > 11) text = text.slice(0, 11) + "..";
            return text;
        },
        mouseEnterStart() {
            this.isMouseInside = true;
            if (this.hoverLock || this.menu.isOpen) {
                return;
            }
            if (this.hoverTimerId) {
                clearTimeout(this.hoverTimerId);
                this.hoverTimerId = null;
            }
            this.hoverTimerId = setTimeout(() => {
                this.toggleHoverMenu(true);
            }, 750);
        },
        mouseLeaveStart() {
            this.isMouseInside = false;
            this.hoverLock = false;
            if (this.menu.isOpen) {
                return;
            }
            if (this.hoverTimerId) {
                clearTimeout(this.hoverTimerId);
                this.hoverTimerId = null;
            }
            this.hoverTimerId = setTimeout(() => {
                this.toggleHoverMenu(false);
            }, 250);
        },
        clickItem(event) {
            // Çift tetiklemeyi önle (touch ve click event'leri için)
            const now = Date.now();
            if (now - this.lastClickTime < 300) {
                if (event) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                return;
            }
            this.lastClickTime = now;

            if (this.hoverTimerId) {
                clearTimeout(this.hoverTimerId);
                this.hoverTimerId = null;
            }

            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            this.toggleMenu();
        },
        closeMenu(item) {
            this.toggleHoverMenu(false);
            this.toggleMenu(false);
        },
        isAllowed(permission) {
            if (this.blockedConnection) return false;
            if (this.adminUser) return true;
            if (this.user.role.isRoot) return true;
            return this.user.role.permissions.map((x) => x.name).includes(permission);
        },
        allowedRoutes() {
            let items = this.getSideBarItems();
            if (this.blockedConnection) return [];
            if (this.adminUser) {
                // Süper admin için: superAdminPageKey olan öğelerde yetki yoksa menüde gösterme
                return items.filter((item) => {
                    if (!item.superAdminPageKey) return true;
                    return this.hasSuperAdminAccess(item.superAdminPageKey);
                });
            }
            if (this.user.role.isRoot) {
                return items.filter((c) => !c.isAdmin);
            }
            try {
                log("perms", {
                    items: items.map((x) => x.permission),
                    userRoles: this.user.role.permissions.map((x) => x.name),
                });
                return items.filter(
                    (menuItem) =>
                        !menuItem.isAdmin &&
                        !!this.user.role.permissions.find((userPermission) => {
                            if (!menuItem.permission) return true;
                            if (Array.isArray(menuItem.permission)) {
                                if (menuItem.permission.includes(userPermission.name)) {
                                    return true;
                                }
                            }
                            if (userPermission !== null && userPermission.name === menuItem.permission) {
                                return true;
                            }
                        })
                );
            } catch (e) {
                console.error(
                    "Some permission with a name could not found! Compare menu items and stored permission keys.",
                    e
                );
            }
        },
        hasSuperAdminAccess(superAdminPageKey) {
            if (!this.adminUser) return true;
            if (this.adminUser.isFullAccess) return true;
            if (!superAdminPageKey) return true;
            return (this.adminUser.approvedPages || []).map((x) => x.page).includes(superAdminPageKey);
        },
        handleMenuClick(item, event) {
            if (this.adminUser && item.superAdminPageKey) {
                if (!this.hasSuperAdminAccess(item.superAdminPageKey)) {
                    event.preventDefault();
                    this.$router.push("/");
                }
            }
        },
        getSideBarItems() {
            return menu(this).filter(
                (x) =>
                    x.enabled &&
                    !["tables", "automations", "form", "ticket", "tasks", "calendar", "chatbot", "mails"].includes(
                        x.path.split("/")[1]
                    )
            );
        },
        handleOutsideClick() {
            if (this.moduleActive) {
                this.moduleActive = false;
            }
        },
        handleWebTabClick(tab, event) {
            // Web tab için yetki kontrolü gerekirse buraya eklenebilir
            // Şimdilik sadece placeholder olarak bırakıyoruz
        },
    },
    beforeDestroy() {
        this.unSubs.forEach((sub) => sub());
        if (this.timerId) clearInterval(this.timerId);
        if (this.timerId2) clearInterval(this.timerId2);
    },
    watch: {
        $route(to, from) {
            this.handleOutsideClick();
            const FirstItem = this.allowedRoutes()[0];
            if (this.$route.path === "/denied" && !this.isUserRoutes && !this.isContentRoutes) {
                this.$router.push(FirstItem.path);
            }
        },
        "menu.isOpen"(newValue, oldValue) {
            if (newValue) {
                this.buttonOpened = true;
                this.hoverLock = false;
            } else {
                if (oldValue && this.buttonOpened && this.isMouseInside) {
                    this.hoverLock = true;
                }
                this.buttonOpened = false;
            }
        },
        "menu.hoverActive"(newValue, oldValue) {
            if (!newValue && oldValue && this.isMouseInside) {
                this.hoverLock = true;
            }
        },
    },
    directives: {
        "click-outside": {
            bind(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener("click", el.clickOutsideEvent);
            },
            unbind(el) {
                document.removeEventListener("click", el.clickOutsideEvent);
            },
        },
    },
};
</script>
<style lang="scss">
.rep-settings-modal {
    .ant-modal {
        width: 760px !important;
    }
}

.hiddenToolTip {
    opacity: 0 !important;
    display: none !important;
}
</style>
<style lang="scss">
.user-popover {
    width: 240px;

    .ant-popover-inner-content {
        width: 100% !important;
    }

    &-content {
        display: flex;
        flex-direction: column;

        &-profile {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px;
            border-bottom: 1px solid var(--border-neutral-default);

            &-content {
                display: flex;
                align-items: center;
                gap: 5px;

                &-name {
                    font-size: 16px;
                }

                &-avatar {
                    width: 35px;
                    height: 35px;

                    .avatar {
                        width: 100%;
                        height: 100%;
                        border-radius: 50%;
                        overflow: hidden;

                        img {
                            object-fit: scale-down;
                        }
                    }
                }
            }

            &-options {
                display: flex;
                align-items: center;
                gap: 5px;

                &-logout {
                    background: var(--color-bg-active);
                    color: var(--color-icon-primary-default);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 30px;
                    height: 30px;
                    border-radius: 5px;
                    cursor: pointer;

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }
        }

        &-item {
            display: flex;
            align-items: center;
            gap: 5px;
            padding: 5px;
            border-bottom: 1px solid var(--border-neutral-default);
            cursor: pointer;

            &.language-content {
                .user-popover-content-item-icon {
                    align-items: flex-end !important;
                }
            }

            &:last-child {
                border-bottom: 0;
            }

            &:hover {
                background: var(--color-bg-neutral-hover);
            }

            &-text {
                width: 100%;
            }

            &-icon,
            &-arrow {
                width: 25px;
                height: 25px;
                display: flex;
                justify-content: center;
                align-items: center;
            }
        }
    }
}
</style>
<style lang="scss" scoped>
.menu-list-item-icon {
    position: relative;

    .new-tag {
        position: absolute;
        background-color: #fe6244;
        color: white;
        padding: 2px 5px;
        font-size: 5px;
        height: 16px;
        border-radius: 50%;
        width: 18px;
        display: flex;
        justify-content: center;
        align-items: center;
        right: -7px;
        top: -8px;
        clip-path: polygon(
            10% 0%,
            20% 10%,
            30% 0%,
            40% 10%,
            50% 0%,
            60% 10%,
            70% 0%,
            80% 10%,
            90% 0%,
            100% 10%,
            100% 20%,
            90% 30%,
            100% 40%,
            90% 50%,
            100% 60%,
            90% 70%,
            100% 80%,
            90% 90%,
            100% 100%,
            90% 100%,
            80% 90%,
            70% 100%,
            60% 90%,
            50% 100%,
            40% 90%,
            30% 100%,
            20% 90%,
            10% 100%,
            0% 90%,
            0% 80%,
            10% 70%,
            0% 60%,
            10% 50%,
            0% 40%,
            10% 30%,
            0% 20%,
            10% 10%,
            0% 0%
        );
        animation: grow-shrink 2s infinite;
    }
}

@keyframes grow-shrink {
    0%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
}

#sidebar {
    //overflow: hidden;
}

#sidebar > ul {
    cursor: pointer;
}

#sidebar > ul > li > ul {
    margin-top: 9px;

    li {
        margin: 4px 0 6px 6px;
    }
}

.myIcon {
    width: 22px;
    height: 22px;
}

.new-theme {
    #sidebar {
        &.open header {
            padding: 0 30px;
            justify-content: space-between;
        }

        header {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 0;
            height: 65px;
            cursor: pointer;

            #arrow-icon {
                transition: all 0.3s;
                transform: rotateY(180deg);

                &.open {
                    transform: rotateY(0deg);
                }
            }

            h1 {
                font-family: "Poppins";
                font-weight: bold;
                font-size: 18px;
                color: #ffffff;
                margin: 0;
                user-select: none;
            }
        }

        ul li a {
            color: #a2a3b7;
            font-family: "Poppins";
            font-size: 13px;

            &.router-link-exact-active.router-link-active {
                color: #ffffff;
            }
        }

        .instagram-icon {
            svg {
                width: 26px !important;
                height: 26px !important;
                border-radius: 4px !important;
                background: radial-gradient(
                    circle at 30% 107%,
                    #fdf497 0%,
                    #fdf497 5%,
                    #fd5949 45%,
                    #d6249f 60%,
                    #285aeb 90%
                ) !important;
            }
        }
    }
}
</style>
